import React from 'react';
import { ExternalLink, ArrowRight, FileText, Calendar, Briefcase } from 'lucide-react';
import { motion } from 'framer-motion';
import { postPlatform, postTribune } from '../../data/mock';
import { useLanguage } from '../../context/LanguageContext';

/**
 * Featured platform section for Smartmedias subsidiary — showcases POST.
 * Includes hero with preview image, brief, products, CTA, and a tribune teaser.
 */
const POSTSection = ({ accentColor = '#D4A54A' }) => {
  const { pick } = useLanguage();
  const productIcons = [Calendar, FileText, Briefcase];

  return (
    <section className="section-padding-lg bg-[#0e1112] border-t border-[rgba(212,165,74,0.2)] relative overflow-hidden">
      {/* Ambient gold glow */}
      <div className="absolute inset-0 pointer-events-none opacity-30">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full blur-3xl"
             style={{ background: `radial-gradient(circle, ${accentColor}55 0%, transparent 70%)` }} />
      </div>

      <div className="container-swg relative z-10">
        {/* Eyebrow */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-16 text-center"
        >
          <span className="caption block mb-4" style={{ color: accentColor }}>Featured Platform</span>
          <h2 className="heading-2 text-white mb-4">
            <span className="font-serif italic">POST</span>
          </h2>
          <p className="text-3xl md:text-4xl mt-6 font-serif italic" style={{ color: accentColor }}>
            "{pick(postPlatform.tagline)}"
          </p>
        </motion.div>

        {/* Hero image + brief */}
        <div className="grid lg:grid-cols-2 gap-12 items-center mb-20">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <a
              href="#" onClick={(e) => e.preventDefault()} aria-disabled="true"
              className="block relative group rounded-2xl overflow-hidden border border-[rgba(212,165,74,0.3)] shadow-[0_20px_60px_-20px_rgba(212,165,74,0.4)] aspect-[16/10]"
              data-testid="post-preview-link"
              style={{
                background: 'radial-gradient(ellipse at center, #1a1f2e 0%, #0a0d14 100%)'
              }}
            >
              {/* Subtle stars/dots */}
              <div className="absolute inset-0 opacity-40" style={{
                backgroundImage: 'radial-gradient(circle at 20% 30%, rgba(255,255,255,0.4) 1px, transparent 1px), radial-gradient(circle at 60% 70%, rgba(255,255,255,0.3) 1px, transparent 1px), radial-gradient(circle at 80% 20%, rgba(255,255,255,0.3) 1px, transparent 1px), radial-gradient(circle at 30% 80%, rgba(255,255,255,0.2) 1px, transparent 1px)',
                backgroundSize: '120px 120px, 180px 180px, 220px 220px, 280px 280px'
              }}></div>

              {/* Diagonal sweep */}
              <div className="absolute inset-0 opacity-40" style={{
                background: 'linear-gradient(120deg, transparent 0%, rgba(212,165,74,0.08) 40%, transparent 70%)'
              }}></div>

              {/* Centered pill */}
              <div className="absolute inset-0 flex items-center justify-center px-8">
                <div className="w-full max-w-[80%] rounded-[3rem] py-12 px-12 backdrop-blur-sm" style={{
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.02) 100%)',
                  border: '1px solid rgba(255,255,255,0.08)'
                }}>
                  <div className="text-center">
                    <h3 className="text-white font-serif text-5xl md:text-6xl tracking-[0.3em] mb-3">POST</h3>
                    <div className="h-px w-20 mx-auto mb-3" style={{ background: 'rgba(255,255,255,0.3)' }} />
                    <p className="text-white text-base tracking-[0.2em] mb-1">The Africa</p>
                    <p className="font-serif italic text-2xl" style={{ color: accentColor }}>We Know</p>
                  </div>
                </div>
              </div>

              {/* Hover badge */}
              <div className="absolute bottom-5 right-5 px-4 py-2 rounded-full backdrop-blur-md text-white text-xs font-medium flex items-center gap-2 opacity-80 group-hover:opacity-100 transition-opacity" style={{
                background: 'rgba(212,165,74,0.15)',
                border: '1px solid rgba(212,165,74,0.4)'
              }}>
                <ExternalLink className="w-3.5 h-3.5" />
                Visit live
              </div>
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.1 }}
          >
            <p className="text-xl text-white leading-relaxed mb-6">
              {pick(postPlatform.shortBrief)}
            </p>
            <p className="text-[#9AACB8] leading-relaxed mb-8">
              {pick(postPlatform.longBrief)}
            </p>

            <a
              href="#" onClick={(e) => e.preventDefault()} aria-disabled="true"
              className="inline-flex items-center gap-3 px-8 py-4 rounded-full font-semibold tracking-wide uppercase text-sm transition-all duration-300 shadow-[0_8px_30px_rgba(212,165,74,0.3)] hover:shadow-[0_12px_40px_rgba(212,165,74,0.5)] hover:scale-[1.03]"
              style={{ background: accentColor, color: '#0e1112' }}
              data-testid="post-cta"
            >
              {pick(postPlatform.cta)}
              <ArrowRight className="w-4 h-4" />
            </a>
          </motion.div>
        </div>

        {/* Products grid */}
        <div className="grid md:grid-cols-3 gap-6 mb-20">
          {postPlatform.products.map((product, index) => {
            const Icon = productIcons[index];
            return (
              <motion.div
                key={product.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="swg-card rounded-2xl p-8 border"
                style={{ borderColor: `${accentColor}30` }}
              >
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-5"
                  style={{ background: `${accentColor}20`, border: `1px solid ${accentColor}40` }}
                >
                  <Icon className="w-5 h-5" style={{ color: accentColor }} />
                </div>
                <h3 className="text-white font-serif text-2xl mb-2">{product.name}</h3>
                <p className="text-[#9AACB8] text-sm leading-relaxed">{pick(product.desc)}</p>
              </motion.div>
            );
          })}
        </div>

        {/* Tribune teaser */}
        <motion.article
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="max-w-3xl mx-auto p-10 rounded-2xl border bg-[rgba(212,165,74,0.05)]"
          style={{ borderColor: `${accentColor}40` }}
          data-testid="post-tribune"
        >
          <div className="flex items-center gap-3 mb-6">
            <span
              className="px-3 py-1 rounded-full text-xs font-bold tracking-[0.2em] uppercase"
              style={{ background: `${accentColor}20`, color: accentColor, border: `1px solid ${accentColor}40` }}
            >
              {pick(postTribune.category)}
            </span>
            <span className="text-[#9AACB8] text-xs">/ Editorial</span>
          </div>

          <h3 className="text-2xl md:text-3xl font-serif text-white mb-6 leading-snug">
            {pick(postTribune.title)}
          </h3>

          <div className="flex items-center gap-3 mb-6 pb-6 border-b border-[rgba(212,165,74,0.2)]">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm"
              style={{ background: accentColor, color: '#0e1112' }}
            >
              {postTribune.author.split(' ').map((w) => w[0]).join('')}
            </div>
            <div>
              <p className="text-white text-sm font-semibold">{postTribune.author}</p>
              <p className="text-[#9AACB8] text-xs">{pick(postTribune.role)}</p>
            </div>
          </div>

          <p className="text-[#9AACB8] leading-relaxed italic mb-6">
            {pick(postTribune.excerpt)}
          </p>

          <a
            href="#" onClick={(e) => e.preventDefault()} aria-disabled="true"
            className="inline-flex items-center gap-2 font-semibold text-sm group"
            style={{ color: accentColor }}
            data-testid="post-tribune-link"
          >
            {pick(postTribune.readMore)}
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </a>
        </motion.article>
      </div>
    </section>
  );
};

export default POSTSection;
